<script lang="ts">
	import "./app.postcss";
	import { onMount } from "svelte";
	import {
		DarkMode,
		Navbar,
		NavBrand,
		NavHamburger,
		Sidebar,
		SidebarGroup,
		SidebarItem,
		SidebarWrapper,
		CloseButton,
		Alert,
		Spinner,
		Button,
		Heading,
		P,
	} from "flowbite-svelte";
	import { sineIn } from "svelte/easing";

	import Router, { push, link, location } from "./svelte-spa-router";

	import routes from "./routes";

	import site from "./site";
	import { Icon } from "flowbite-svelte-icons";

	import { isConfigurationLoaded, configurationError } from "./AppConfiguration";
	import AbstractID, {
		isAuthorizationPending,
		isAuthorized,
		colorTheme,
	} from "./AbstractID";
	import Drawer from "./components/Drawer.svelte";
	import AbstractIdCard from "./components/AbstractIDCard.svelte";

	// Public status page is the anonymous root; admin lives under /Admin/*.
	$: isAdmin = $location.startsWith("/Admin");
	let loginTriggered = false;
	// Entering the admin area while unauthenticated sends the user to AbstractID.
	$: if (isAdmin && !loginTriggered && !$isAuthorizationPending && !$isAuthorized) {
		loginTriggered = true;
		AbstractID.navigateToLogin();
	}

	const breakPoint: number = 640;
	let width: number = breakPoint;
	let isSidebarBackdropEnabled: boolean = true;
	let isSidebarHideOnClickOutside = true;
	let drawerHidden: boolean = false;

	$: if (width >= breakPoint) {
		drawerHidden = false;
		isSidebarHideOnClickOutside = false;
		isSidebarBackdropEnabled = false;
	} else {
		drawerHidden = true;
		isSidebarHideOnClickOutside = true;
		isSidebarBackdropEnabled = true;
	}
	onMount(() => {
		drawerHidden = width < breakPoint;
		isSidebarHideOnClickOutside = width < breakPoint;
	});

	const fnToggleSide = (event?: Event, href: any = undefined) => {
		if (width < breakPoint) drawerHidden = !drawerHidden;
		if (href) {
			event?.preventDefault();
			push(href);
		}
	};
	const fnToggleDrawer = () => { drawerHidden = false; };

	function toggleTheme() {
		$colorTheme = $colorTheme === "dark" ? "light" : "dark";
		window.document.documentElement.classList.toggle("dark", $colorTheme === "dark");
	}

	$: activeUrl = $location;

	const adminNav = [
		{ label: "Dashboard", href: "/Admin", icon: "home-outline" },
		{ label: "Services", href: "/Admin/Services", icon: "grid-outline" },
		{ label: "Groups", href: "/Admin/Groups", icon: "grid-plus-outline" },
		{ label: "Probes", href: "/Admin/Probes", icon: "chart-line-up-outline" },
		{ label: "Metrics", href: "/Admin/Metrics", icon: "chart-mixed-outline" },
		{ label: "Incidents", href: "/Admin/Incidents", icon: "exclamation-circle-outline" },
		{ label: "Alerts", href: "/Admin/Alerts", icon: "bell-outline" },
	];
</script>

<svelte:window bind:innerWidth={width} />

{#if isAdmin}
	<!-- Authenticated admin shell (existing template layout) -->
	<header class="flex-none w-full mx-auto bg-white dark:bg-zinc-800">
		<Navbar class="bg-zinc-100 dark:bg-zinc-800 border-solid border-t-[4px] border-primary-500" hidden={!drawerHidden}>
			<NavHamburger on:click={fnToggleDrawer} />
			<NavBrand href="/Admin" class="lg:ml-64">
				<span class="text-xl font-bold dark:text-white">{site.name} Admin</span>
			</NavBrand>
			<div class="flex items-center ml-auto">
				<DarkMode class="inline-block text-black dark:hover:text-white hover:text-gray-900" />
			</div>
		</Navbar>
	</header>

	<Drawer
		transitionType="fly"
		divClass="overflow-y-auto z-40 bg-white text-white dark:bg-zinc-800"
		transitionParams={{ x: -250, duration: 200, easing: sineIn }}
		bind:backdrop={isSidebarBackdropEnabled}
		bind:hidden={drawerHidden}
		bind:activateClickOutside={isSidebarHideOnClickOutside}
		width="w-[250px]"
		class="overflow-scroll pb-32"
		id="sidebar"
	>
		<div class="flex items-center">
			<CloseButton on:click={() => (drawerHidden = true)} class="mt-2 -mb-4 text-white sm:hidden" />
		</div>
		<Sidebar asideClass="w-54" {activeUrl}>
			<SidebarWrapper divClass="overflow-y-auto py-4 px-4 rounded">
				{@const activeClass = "flex items-center p-2 text-base font-normal text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 active"}
				{@const nonActiveClass = "flex items-center p-2 text-base font-normal text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"}
				{@const iconClass = "w-5 h-5 min-w-5 min-h-5 shrink-0 text-primary-500 dark:text-primary-500"}
				{@const labelClass = "ml-3 truncate"}

				<a title={site.name} href="/" use:link class="block mb-3 text-center font-bold uppercase text-xs tracking-wide text-zinc-800 dark:text-zinc-300">
					← Public status page
				</a>

				{#if $isAuthorized}
					<AbstractIdCard compact on:click={() => {}} />
				{/if}

				{#if !$configurationError}
					<SidebarGroup ulClass="mt-2 appNav">
						{#each adminNav as item}
							<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} label={item.label} href={item.href} on:click={(event) => fnToggleSide(event, item.href)}>
								<svelte:fragment slot="icon"><Icon name={item.icon} class={iconClass} /></svelte:fragment>
							</SidebarItem>
						{/each}
					</SidebarGroup>

					{#if $isAuthorized && AbstractID.isAdministrator}
						<SidebarGroup ulClass="mt-0 appNav">
							<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} label="Users" href="/Admin/Users" on:click={(event) => fnToggleSide(event, "/Admin/Users")}>
								<svelte:fragment slot="icon"><Icon name="users-outline" class={iconClass} /></svelte:fragment>
							</SidebarItem>
						</SidebarGroup>
					{/if}

					<SidebarGroup ulClass="mt-0 appNav">
						<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} label="Settings" href="/Admin/Settings" on:click={(event) => fnToggleSide(event, "/Admin/Settings")}>
							<svelte:fragment slot="icon"><Icon name="cog-outline" class={iconClass} /></svelte:fragment>
						</SidebarItem>
					</SidebarGroup>
				{/if}
			</SidebarWrapper>
		</Sidebar>

		<div class="text-gray-600 dark:text-gray-500 text-sm text-center mt-8">
			<Button class="p-2" color="alternative" on:click={toggleTheme}>
				<Icon name={$colorTheme === "dark" ? "sun-solid" : "moon-solid"} class="w-4 h-4" />
			</Button>
		</div>
	</Drawer>

	<div class="flex px-4 mx-0 max-w-screen-2xl w-full">
		<main class="mt-10 mb-40 sm:ml-64 xl:ml-72 w-full mx-auto dark:text-white">
			{#if !$isAuthorizationPending && $isConfigurationLoaded && $isAuthorized && AbstractID.isUserApplicationAccessRoleAvailable}
				<Router {routes} restoreScrollState={true} on:conditionsFailed={() => push("/Admin")} />
			{:else if $isAuthorized && !AbstractID.isUserApplicationAccessRoleAvailable}
				<Alert>
					<Heading tag="h1"><Icon name="lock-solid" class="inline w-12 h-12 pb-3 mr-2" />Access Denied</Heading>
					<P>You don't have permission to access {site.name}. Please contact an administrator.</P>
				</Alert>
			{:else if ($configurationError ?? "").length > 0}
				<Alert>
					<Heading tag="h1"><Icon name="bug-solid" class="inline w-12 h-12 pb-3 mr-2" />No Backend Response</Heading>
					<P>{site.name} is currently unavailable. Please try again later.</P>
				</Alert>
			{:else}
				<Spinner />
			{/if}
		</main>
	</div>
{:else}
	<!-- Public, anonymous status page -->
	<div class="public-shell min-h-screen bg-white dark:bg-zinc-900 text-gray-900 dark:text-white flex flex-col">
		<header class="border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
			<div class="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
				<button class="text-lg font-bold" on:click={() => push("/")}>{site.name}</button>
				<div class="flex items-center gap-2">
					<a href="/Incidents" use:link class="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white">Incident History</a>
					<Button size="xs" color="alternative" class="p-2" on:click={toggleTheme}>
						<Icon name={$colorTheme === "dark" ? "sun-solid" : "moon-solid"} class="w-4 h-4" />
					</Button>
				</div>
			</div>
		</header>

		<main class="flex-1 w-full max-w-3xl mx-auto px-4 py-8">
			<Router {routes} restoreScrollState={true} on:conditionsFailed={() => push("/")} />
		</main>

		<footer class="border-t border-gray-200 dark:border-zinc-800 py-6">
			<div class="max-w-3xl mx-auto px-4 text-center text-xs text-gray-400">
				<p>Powered by {site.name}</p>
			</div>
		</footer>
	</div>
{/if}
